import { useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import type { ResultOf } from 'gql.tada';
import {
  Calendar,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Home,
  Mail,
  PenLine,
  Receipt,
  Send,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { Button } from '~/components/ui/button';
import { familyLabel } from '~/utils/familyLabel';
import { isCurrentYear, isPastYear } from '~/utils/schoolYearState';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import type { AppLayoutContext } from '~/types';
import { useSendFamilyInvites } from '~/pages/Enrollments/useSendFamilyInvites';
import { phasesFor, type Phase } from '~/pages/Enrollments/enrollmentPhases';
import { AdminInboxQuery, useAdminInboxQuery } from './useAdminInboxQuery';
import './Dashboard.css';

type InboxResult = NonNullable<NonNullable<ResultOf<typeof AdminInboxQuery>>['schoolYear']>['adminInbox'];
type InboxItem = InboxResult['items'][number];
type Pipeline = InboxResult['pipeline'];

const ITEM_ICONS: Record<InboxItem['kind'], typeof Send> = {
  INVITE_READY: Send,
  NEEDS_REVIEW: ClipboardCheck,
  PACKET_UNSIGNED: PenLine,
  INVOICE_OVERDUE: Receipt,
  INQUIRIES_WAITING: Mail,
  ATTENDANCE_MISSING: CalendarClock,
  HOUSEHOLD_CHANGE: Home,
  NEW_CHILDREN: UserPlus,
  TUITION_TO_SET: Wallet,
  TUITION_TO_CHANGE: Wallet,
};

const ITEM_ACTIONS: Record<InboxItem['kind'], { label: string; primary?: boolean }> = {
  INVITE_READY: { label: 'Send invite', primary: true },
  NEEDS_REVIEW: { label: 'Review', primary: true },
  PACKET_UNSIGNED: { label: 'Open enrollment' },
  INVOICE_OVERDUE: { label: 'View invoices' },
  INQUIRIES_WAITING: { label: 'Open inquiries' },
  ATTENDANCE_MISSING: { label: 'Attendance report' },
  HOUSEHOLD_CHANGE: { label: 'Review changes', primary: true },
  NEW_CHILDREN: { label: 'Decide', primary: true },
  TUITION_TO_SET: { label: 'Set tuition', primary: true },
  TUITION_TO_CHANGE: { label: 'Change tuition', primary: true },
};

const URGENCY_CLASSES: Record<string, string> = {
  danger: 'text-red-600 font-medium',
  warning: 'text-orange-600 font-medium',
  normal: 'text-muted-foreground',
};

/** The pipeline's count for each of the board's columns. */
const PIPELINE_KEYS: Record<Phase, keyof Pipeline> = {
  setup: 'setup',
  open: 'open',
  in_review: 'inReview',
  offered: 'offered',
  complete: 'complete',
  closed: 'closed',
};

/** "Today", "Since yesterday", "Waiting 3 days" — how long an item has sat in the inbox. */
// Remembered per browser: an admin who hides the inbox from passers-by
// should not have to hide it again every time the dashboard loads.
const INBOX_HIDDEN_KEY = 'solastack.dashboard.inboxHidden';

function readInboxHidden(): boolean {
  try {
    return localStorage.getItem(INBOX_HIDDEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeInboxHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.setItem(INBOX_HIDDEN_KEY, 'true');
    else localStorage.removeItem(INBOX_HIDDEN_KEY);
  } catch {
    // Storage can be unavailable (private window); the toggle still works for this visit.
  }
}

export function formatWaiting(since: string | null | undefined, now: Date = new Date()): string {
  if (!since) return '';
  const start = new Date(since);
  const days = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Since yesterday';
  return `Waiting ${days} days`;
}

function Dashboard() {
  const { school, roleContext, setSelection } = useOutletContext<AppLayoutContext>();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<string | null>(null);
  const [inboxHidden, setInboxHidden] = useState(readInboxHidden);
  const toggleInbox = () => {
    setInboxHidden((hidden) => {
      writeInboxHidden(!hidden);
      return !hidden;
    });
  };

  const isAdmin = ['PRIMARY_ADMIN', 'ADMIN'].includes(roleContext.selectedRole ?? '');
  const selectedYear = roleContext.selectedSchoolYear;

  const { data, loading, error, refetch } = useAdminInboxQuery(isAdmin ? selectedYear?.id : undefined);
  const [sendFamilyInvites, { loading: sendingInvite }] = useSendFamilyInvites();

  const inbox = data?.schoolYear?.adminInbox;
  // The pipeline shows the board's columns, which follow the switch. Declined
  // families are counted in its header instead of a tile.
  const checklistEnrollmentOn = !!data?.schoolYear?.school?.checklistEnrollmentOn;
  const pipelinePhases = phasesFor(checklistEnrollmentOn).filter((phase) => phase.key !== 'closed');
  const yearSlug = data?.schoolYear?.slug ?? selectedYear?.slug;

  // A saved year selection can point at a past year, where there's nothing
  // left to do. Next year, still being planned, is where re-enrollment happens,
  // so only a past year gets the note.
  const activeYear = useMemo(() => {
    const accessibleSchool = roleContext.accessibleSchools?.find((as) => as.school.id === roleContext.selectedSchool?.id);
    return accessibleSchool?.accessibleSchoolYears.map((asy) => asy.schoolYear).find(isCurrentYear) ?? null;
  }, [roleContext]);
  const viewingPastYear = !!selectedYear && !!activeYear && activeYear.id !== selectedYear.id && isPastYear(data?.schoolYear);

  const today = useMemo(
    () => new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }),
    []
  );

  if (!school) {
    return <p>Loading...</p>;
  }

  const enrollmentsPath = (params?: Record<string, string>) => {
    const search = params ? `?${new URLSearchParams(params).toString()}` : '';
    return `/app/${school.slug}/years/${yearSlug}/enrollments${search}`;
  };

  const handleItemAction = (item: InboxItem) => {
    const enrollment = item.familyEnrollment;
    switch (item.kind) {
      case 'INVITE_READY':
        if (!enrollment) return;
        sendFamilyInvites({
          variables: { familyEnrollmentIds: [enrollment.id] },
          onCompleted: (result) => {
            const count = result?.sendFamilyInvites?.sentCount || 0;
            setNotice(count > 0 ? `Invite sent to the ${familyLabel(enrollment.family.name)}.` : 'The invite could not be sent.');
            setTimeout(() => setNotice(null), 5000);
            refetch();
          },
        });
        return;
      case 'NEEDS_REVIEW':
      case 'PACKET_UNSIGNED':
        if (enrollment) navigate(enrollmentsPath({ tab: 'families', family: enrollment.id }));
        return;
      case 'INVOICE_OVERDUE':
        if (enrollment) navigate(`/app/${school.slug}/families/${enrollment.family.slug}/billing`);
        return;
      case 'INQUIRIES_WAITING':
        navigate(enrollmentsPath({ tab: 'inquiries' }));
        return;
      case 'ATTENDANCE_MISSING':
        navigate(`/app/${school.slug}/years/${yearSlug}/schedule`);
        return;
      case 'NEW_CHILDREN':
        // One family goes to its page; several go to the year's students, filtered to the undecided.
        if (item.family) {
          navigate(`/app/${school.slug}/families/${item.family.slug}/enrollment`);
        } else {
          navigate(enrollmentsPath({ tab: 'students', student_f_state: 'PENDING_ADMISSIONS_REVIEW' }));
        }
        return;
      case 'TUITION_TO_SET':
      case 'TUITION_TO_CHANGE':
        if (item.family) navigate(`/app/${school.slug}/families/${item.family.slug}/enrollment`);
        return;
      case 'HOUSEHOLD_CHANGE':
        if (item.family) navigate(`/app/${school.slug}/families/${item.family.slug}`);
        return;
    }
  };

  // Family members land here too; they get the year summary, not the admin inbox.
  if (!isAdmin) {
    const yearCount = school.schoolYears?.length ?? 0;
    return (
      <div className="dashboard p-6">
        <h2 className="text-2xl font-semibold mb-6">Dashboard</h2>
        <Card className="max-w-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">School Years</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{yearCount}</div>
            <p className="text-xs text-muted-foreground">
              {yearCount === 0 ? 'No school years created yet' : 'Total school years'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="dashboard p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Dashboard</h2>
        <p className="text-muted-foreground">
          {today}
          {selectedYear ? ` · ${selectedYear.name}` : ''}
          {inbox ? ` · ${inbox.pipeline.enrolledStudents} students enrolled` : ''}
        </p>
      </div>

      {notice && (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" role="status">
          {notice}
        </div>
      )}

      {viewingPastYear && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-900" role="note">
          <span>
            You're looking at <strong>{selectedYear.name}</strong>, which isn't the current school year, so there's nothing to act on here.
          </span>
          <Button size="sm" variant="outline" onClick={() => setSelection({ schoolYearId: activeYear.id })}>
            Switch to {activeYear.name}
          </Button>
        </div>
      )}

      {!selectedYear && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Pick a school year in the header to see what needs you.
          </CardContent>
        </Card>
      )}

      {selectedYear && error && (
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            Error loading your inbox: {error.message}
          </CardContent>
        </Card>
      )}

      {selectedYear && !error && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="flex items-baseline gap-2.5">
              <CardTitle className="text-lg">Needs you</CardTitle>
              {inbox && (
                <span className="text-sm text-muted-foreground">
                  {inbox.items.length === 0 ? 'nothing waiting' : `${inbox.items.length} ${inbox.items.length === 1 ? 'item' : 'items'}`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!inboxHidden && inbox && inbox.items.length > 0 && (
                <span className="text-xs text-muted-foreground">Sorted by how long it has been waiting</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={toggleInbox}
                aria-expanded={!inboxHidden}
                aria-controls="dashboard-inbox"
                aria-label={inboxHidden ? 'Show what needs you' : 'Hide what needs you'}
                title={inboxHidden ? 'Show' : 'Hide'}
              >
                {inboxHidden ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          {!inboxHidden && (
            <CardContent id="dashboard-inbox" className="p-0">
              {loading && !inbox && (
                <div className="px-6 pb-6 text-sm text-muted-foreground">Loading...</div>
              )}
              {inbox && inbox.items.length === 0 && (
                <div className="flex items-center gap-3 px-6 pb-6 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Nothing needs you right now.
                </div>
              )}
              {inbox && inbox.items.length > 0 && (
                <ul className="divide-y border-t">
                  {inbox.items.map((item, index) => {
                    const Icon = ITEM_ICONS[item.kind];
                    const action = ITEM_ACTIONS[item.kind];
                    const busy = item.kind === 'INVITE_READY' && sendingInvite;
                    return (
                      <li key={`${item.kind}-${item.familyEnrollment?.id ?? index}`} className="flex items-center gap-4 px-5 py-3.5">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium leading-5">{item.title}</p>
                          <p className="text-sm text-muted-foreground leading-5">{item.detail}</p>
                        </div>
                        <span className={`w-28 shrink-0 text-right text-xs ${URGENCY_CLASSES[item.urgency] ?? URGENCY_CLASSES.normal}`}>
                          {/* Overdue rows already say how late they are in the detail line. */}
                          {item.kind === 'INVOICE_OVERDUE' ? 'Overdue' : formatWaiting(item.since as string | null)}
                        </span>
                        <Button
                          variant={action.primary ? 'default' : 'outline'}
                          size="sm"
                          className="shrink-0"
                          disabled={busy}
                          onClick={() => handleItemAction(item)}
                        >
                          {busy ? 'Sending...' : action.label}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {selectedYear && inbox && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-baseline gap-2.5">
              <CardTitle className="text-lg">Enrollment pipeline</CardTitle>
              <span className="text-sm text-muted-foreground">
                {selectedYear.name}
                {inbox.pipeline.closed > 0 ? ` · ${inbox.pipeline.closed} declined` : ''}
              </span>
            </div>
            <Button variant="link" className="h-auto p-0 font-medium" onClick={() => navigate(enrollmentsPath({ tab: 'families' }))}>
              Open the board
              <ChevronRight className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-2 gap-3 ${checklistEnrollmentOn ? 'md:grid-cols-3' : 'md:grid-cols-5'}`}>
              {pipelinePhases.map(({ key: phase, label }) => {
                const key = PIPELINE_KEYS[phase];
                const count = inbox.pipeline[key] as number;
                const highlight = key === 'inReview' && count > 0;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => navigate(enrollmentsPath({ tab: 'families', phase }))}
                    className={`flex flex-col gap-1 rounded-md border px-4 py-3.5 text-left transition-colors hover:bg-muted/50 ${
                      highlight ? 'border-orange-200 bg-orange-50 hover:bg-orange-100/60' : ''
                    }`}
                  >
                    <span className={`text-xs font-semibold uppercase tracking-wide ${highlight ? 'text-orange-700' : 'text-muted-foreground'}`}>
                      {label}
                    </span>
                    <span className={`text-2xl font-bold ${highlight ? 'text-orange-600' : key === 'complete' && count > 0 ? 'text-green-600' : ''}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default Dashboard;
